import React, { useState, useEffect } from 'react';
import { 
  Calendar, Search, Filter, DollarSign, FileText, Clock, CheckCircle, 
  AlertTriangle, Edit3, Trash2, Download, Upload, Eye, Plus, 
  ArrowUpDown, MoreHorizontal, RefreshCw, Settings
} from 'lucide-react';
import api from '../../services/api';
import PaymentManagementModal from './PaymentManagementModal';
import MultiSearchableSelect from '../ui/MultiSearchableSelect';

interface PaymentItem {
  id: number;
  itinerary_id: number;
  vendor_name: string;
  vendor_id: number; // Make this required instead of optional
  due_date: string;
  item_count: number;
  amount: number;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  guest_name: string;
  payment_categories: string;
  item_names: string;
  item_ids: number[];
  outstanding_amount: number;
  total_paid: number;
  days_overdue?: number;
  status: string;
  item_name?: string;
  payment_category?: string;
  groupItems?: any;
  due_date_to?: string;
}

const AccountsV3Dashboard: React.FC = () => {
  const [paymentItems, setPaymentItems] = useState<PaymentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');
  const [sortConfig, setSortConfig] = useState({ key: 'due_date', direction: 'asc' });
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    vendor: [] as string[],
    vendorFilterMode: 'include' as 'include' | 'exclude',
    dateRange: '',
    overdue: false,
    search_query: '',
    due_date_from: '',
    due_date_to: new Date().toISOString().split('T')[0], // Default to today
    travel_date_from: '',
    travel_date_to: '',
    payment_status: 'PENDING_PAYMENT', // Default to Pending Payment
    itinerary_id: '',
    lead_guest_name: ''
  });
  const [vendors, setVendors] = useState<string[]>([]);
  const [selectedPaymentItem, setSelectedPaymentItem] = useState<PaymentItem | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showFilters, setShowFilters] = useState(true); // Default expanded

  // Quick Stats
  const stats = {
    total_pending: paymentItems.filter(item => item.status === 'PENDING_PAYMENT').length,
    overdue: paymentItems.filter(item => item.days_overdue && item.days_overdue > 0).length,
    due_today: paymentItems.filter(item => {
      const today = new Date().toISOString().split('T')[0];
      return item.due_date === today;
    }).length,
    total_outstanding: paymentItems.reduce((sum, item) => sum + item.outstanding_amount, 0)
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getPriorityColor = (priority: string, overdue?: number) => {
    if (overdue && overdue > 0) return 'text-red-600 bg-red-50';
    switch (priority) {
      case 'HIGH': return 'text-red-600 bg-red-50';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-50';
      case 'LOW': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusColor = (status: string) => {
    // If filter is set to PENDING_INVOICE and status is PENDING_PAYMENT, use purple color
    if (filters.payment_status === 'PENDING_INVOICE' && status === 'PENDING_PAYMENT') {
      return 'bg-purple-100 text-purple-800';
    }
    
    // Handle BOOKED status from database - should show as purple (PENDING INVOICE)
    if (status === 'BOOKED') {
      return 'bg-purple-100 text-purple-800';
    }
    
    switch (status) {
      case 'PENDING_PAYMENT': return 'bg-red-100 text-red-800';
      case 'PARTIALLY_PAID': return 'bg-yellow-100 text-yellow-800';
      case 'PAYMENT_COMPLETED': return 'bg-green-100 text-green-800';
      case 'PENDING_INVOICE': return 'bg-purple-100 text-purple-800';
      case 'OVERDUE': return 'bg-red-200 text-red-900';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    // If filter is set to PENDING_INVOICE and status is PENDING_PAYMENT, show as PENDING INVOICE
    if (filters.payment_status === 'PENDING_INVOICE' && status === 'PENDING_PAYMENT') {
      return 'PENDING INVOICE';
    }
    
    // Handle BOOKED status from database - should show as PENDING INVOICE
    if (status === 'BOOKED') {
      return 'PENDING INVOICE';
    }
    
    switch (status) {
      case 'PENDING_PAYMENT': return 'PENDING PAYMENT';
      case 'PARTIALLY_PAID': return 'PARTIALLY PAID';
      case 'PAYMENT_COMPLETED': return 'PAYMENT COMPLETED';
      case 'PENDING_INVOICE': return 'PENDING INVOICE';
      case 'OVERDUE': return 'OVERDUE';
      default: return status.replace(/_/g, ' ');
    }
  };

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleBulkAction = async (action: string) => {
    if (selectedItems.size === 0) return;
    
    try {
      await api.post('/accounts-v3/bulk-action', {
        action,
        item_ids: Array.from(selectedItems)
      });
      // Refresh data
      loadPaymentItems();
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Bulk action failed:', error);
    }
  };

  const loadPaymentItems = async () => {
    try {
      setLoading(true);
      const response = await api.post('/accounts-v3/payment-items', { filters, sort: sortConfig });
      if (response.data.success) {
        setPaymentItems(response.data.data);
      }
    } catch (error) {
      console.error('Error loading payment items:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadVendors = async () => {
    try {
      const response = await api.get('/accounts-v3/vendors');
      if (response.data.success) {
        setVendors(response.data.data);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const handlePaymentItemClick = async (item: PaymentItem) => {
    try {
      // For grouped items, pass the group data directly to modal
      const groupPaymentItem = {
        ...item,
        item_name: `${item.vendor_name} - ${item.item_count} items`,
        payment_category: item.payment_categories,
        vendor_id: item.vendor_id, // Use the actual vendor_id from the item
        groupItems: item.item_ids, // Pass the item IDs for detailed loading
        due_date_to: filters.due_date_to // Pass the current due date filter
      };
      setSelectedPaymentItem(groupPaymentItem);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Error preparing group details:', error);
      setSelectedPaymentItem({
        ...item,
        due_date_to: filters.due_date_to // Also pass for fallback
      });
      setShowPaymentModal(true);
    }
  };

  useEffect(() => {
    // Load when filters or sortConfig changes
    loadPaymentItems();
  }, [filters, sortConfig]);

  useEffect(() => {
    loadVendors();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Accounts Management V3</h1>
              <p className="mt-1 text-xs sm:text-sm text-gray-500">Comprehensive payment tracking and management</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setViewMode(viewMode === 'table' ? 'kanban' : 'table')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                {viewMode === 'table' ? 'Kanban View' : 'Table View'}
              </button>
              <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                <Settings className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mb-4 sm:mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-600 text-sm font-medium">Pending</p>
                  <p className="text-2xl font-bold text-red-700">{stats.total_pending}</p>
                </div>
                <Clock className="h-8 w-8 text-red-500" />
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-600 text-sm font-medium">Overdue</p>
                  <p className="text-2xl font-bold text-orange-700">{stats.overdue}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-500" />
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-medium">Due Today</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.due_today}</p>
                </div>
                <Calendar className="h-8 w-8 text-blue-500" />
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-medium">Outstanding</p>
                  <p className="text-lg font-bold text-green-700">{formatCurrency(stats.total_outstanding)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-4 sm:mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
          {/* First Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Itinerary ID
              </label>
              <input
                type="text"
                placeholder="WH_4989"
                value={filters.itinerary_id}
                onChange={(e) => setFilters({...filters, itinerary_id: e.target.value})}
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Lead Guest Name
              </label>
              <input
                type="text"
                placeholder="Guest name"
                value={filters.lead_guest_name}
                onChange={(e) => setFilters({...filters, lead_guest_name: e.target.value})}
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Payment Status
              </label>
              <select 
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                value={filters.payment_status}
                onChange={(e) => setFilters({...filters, payment_status: e.target.value})}
              >
                <option value="">All Payment Status</option>
                <option value="PAYMENT_COMPLETED">Completed</option>
                <option value="PENDING_PAYMENT">Pending Payment</option>
                <option value="PENDING_INVOICE">Pending Invoice (Booked)</option>
                <option value="OVERDUE">Overdue</option>
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Vendor
              </label>
              <div className="flex items-center gap-2 mb-1">
                <button
                  type="button"
                  onClick={() => setFilters({...filters, vendorFilterMode: filters.vendorFilterMode === 'include' ? 'exclude' : 'include'})}
                  className={`px-2 py-0.5 text-xs font-medium rounded-full border transition-colors ${filters.vendorFilterMode === 'include' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-red-100 text-red-700 border-red-300'}`}
                >
                  {filters.vendorFilterMode === 'include' ? '✓ Include' : '✕ Exclude'}
                </button>
              </div>
              <MultiSearchableSelect
                options={vendors.filter(v => v !== 'All Vendors').map(vendor => ({ value: vendor, label: vendor }))}
                value={filters.vendor}
                onChange={(value) => setFilters({...filters, vendor: value as string[]})}
                placeholder="Select vendors..."
                className="text-sm"
              />
            </div>
          </div>
          
          {/* Second Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Due Date From
              </label>
              <input
                type="date"
                value={filters.due_date_from}
                onChange={(e) => setFilters({...filters, due_date_from: e.target.value})}
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Due Date To
              </label>
              <input
                type="date"
                value={filters.due_date_to}
                onChange={(e) => setFilters({...filters, due_date_to: e.target.value})}
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Travel Date From
              </label>
              <input
                type="date"
                value={filters.travel_date_from}
                onChange={(e) => setFilters({...filters, travel_date_from: e.target.value})}
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Travel Date To
              </label>
              <input
                type="date"
                value={filters.travel_date_to}
                onChange={(e) => setFilters({...filters, travel_date_to: e.target.value})}
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
          
          {/* Third Row - Search Button */}
          <div>
            <button
              onClick={loadPaymentItems}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-medium flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              Search
            </button>
          </div>

          {/* Clear Filters */}
          {(filters.itinerary_id || filters.lead_guest_name || filters.due_date_from || filters.due_date_to !== new Date().toISOString().split('T')[0] || filters.travel_date_from || filters.travel_date_to || filters.payment_status !== 'PENDING_PAYMENT' || filters.vendor.length > 0) && (
            <div className="mt-3">
              <button
                onClick={() => {
                  setFilters({
                    status: '',
                    priority: '',
                    vendor: [] as string[],
                    vendorFilterMode: 'include' as 'include' | 'exclude',
                    dateRange: '',
                    overdue: false,
                    search_query: '',
                    due_date_from: '',
                    due_date_to: new Date().toISOString().split('T')[0],
                    travel_date_from: '',
                    travel_date_to: '',
                    payment_status: 'PENDING_PAYMENT',
                    itinerary_id: '',
                    lead_guest_name: ''
                  });
                }}
                className="text-xs sm:text-sm text-blue-600 hover:text-blue-800"
              >
                Reset to defaults
              </button>
            </div>
          )}

          {/* Bulk Actions */}
          {selectedItems.size > 0 && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{selectedItems.size} selected</span>
                <button
                  onClick={() => handleBulkAction('mark_paid')}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                >
                  Mark Paid
                </button>
                <button
                  onClick={() => handleBulkAction('send_reminder')}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Send Reminder
                </button>
                <button
                  onClick={() => handleBulkAction('export')}
                  className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                >
                  Export
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Payment Items Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Mobile Card View */}
          <div className="block sm:hidden">
            <div className="divide-y divide-gray-200">
              {paymentItems.map((item) => (
                <div 
                  key={item.id} 
                  className={`p-4 hover:bg-gray-50 cursor-pointer ${selectedItems.has(item.id) ? 'bg-blue-50' : ''}`}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('input[type="checkbox"]') || 
                        (e.target as HTMLElement).closest('.action-buttons')) {
                      return;
                    }
                    handlePaymentItemClick(item);
                  }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedItems);
                          if (e.target.checked) {
                            newSelected.add(item.id);
                          } else {
                            newSelected.delete(item.id);
                          }
                          setSelectedItems(newSelected);
                        }}
                        className="rounded border-gray-300"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">WH_{item.itinerary_id}</div>
                        <div className="text-xs text-gray-500">{item.guest_name}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 action-buttons">
                      <button 
                        onClick={() => handlePaymentItemClick(item)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Manage Group"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handlePaymentItemClick(item)}
                        className="text-green-600 hover:text-green-900 p-1"
                        title="Add Payment"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-900">{item.vendor_name}</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(item.priority, item.days_overdue)}`}>
                        {item.priority}
                      </span>
                    </div>
                    
                    <div className="text-xs text-blue-600">{item.payment_categories}</div>
                    <div className="text-xs text-gray-500">{item.item_count} items</div>
                    
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</div>
                        {item.total_paid > 0 && (
                          <div className="text-xs text-green-600">Paid: {formatCurrency(item.total_paid)}</div>
                        )}
                        {item.outstanding_amount > 0 && (
                          <div className="text-xs text-red-600">Outstanding: {formatCurrency(item.outstanding_amount)}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {new Date(item.due_date).toLocaleDateString('en-GB')}
                        </div>
                        {item.days_overdue && item.days_overdue > 0 && (
                          <div className="text-xs text-red-600">
                            {item.days_overdue} days overdue
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                        {getStatusText(item.status)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems(new Set(paymentItems.map(item => item.id)));
                        } else {
                          setSelectedItems(new Set());
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('due_date')}
                  >
                    <div className="flex items-center gap-1">
                      Due Date
                      <ArrowUpDown className="h-4 w-4" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('itinerary_id')}
                  >
                    <div className="flex items-center gap-1">
                      Itinerary
                      <ArrowUpDown className="h-4 w-4" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Items & Categories
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('amount')}
                  >
                    <div className="flex items-center gap-1">
                      Amount
                      <ArrowUpDown className="h-4 w-4" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paymentItems.map((item) => (
                  <tr 
                    key={item.id} 
                    className={`hover:bg-gray-50 cursor-pointer ${selectedItems.has(item.id) ? 'bg-blue-50' : ''}`}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('input[type="checkbox"]') || 
                          (e.target as HTMLElement).closest('.action-buttons')) {
                        return;
                      }
                      handlePaymentItemClick(item);
                    }}
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedItems);
                          if (e.target.checked) {
                            newSelected.add(item.id);
                          } else {
                            newSelected.delete(item.id);
                          }
                          setSelectedItems(newSelected);
                        }}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {new Date(item.due_date).toLocaleDateString('en-GB')}
                      </div>
                      {item.days_overdue && item.days_overdue > 0 && (
                        <div className="text-xs text-red-600">
                          {item.days_overdue} days overdue
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(item.priority, item.days_overdue)}`}>
                        {item.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">WH_{item.itinerary_id}</div>
                      <div className="text-sm text-gray-500 truncate max-w-32" title={item.guest_name}>
                        {item.guest_name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{item.vendor_name}</div>
                      <div className="text-xs text-blue-600">{item.payment_categories}</div>
                      <div className="text-xs text-gray-500">{item.item_count} items</div>
                      <div className="text-xs text-gray-400 truncate max-w-xs" title={item.item_names}>
                        {item.item_names}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</div>
                      {item.total_paid > 0 && (
                        <div className="text-xs text-green-600">
                          Paid: {formatCurrency(item.total_paid)}
                        </div>
                      )}
                      {item.outstanding_amount > 0 && (
                        <div className="text-xs text-red-600">
                          Outstanding: {formatCurrency(item.outstanding_amount)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                        {getStatusText(item.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2 action-buttons">
                        <button 
                          onClick={() => handlePaymentItemClick(item)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Manage Group"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handlePaymentItemClick(item)}
                          className="text-green-600 hover:text-green-900"
                          title="Add Payment"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        <button className="text-gray-600 hover:text-gray-900">
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button className="text-gray-600 hover:text-gray-900">
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Payment Management Modal */}
      <PaymentManagementModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedPaymentItem(null);
        }}
        paymentItem={selectedPaymentItem}
        onUpdate={loadPaymentItems}
      />
    </div>
  );
};

export default AccountsV3Dashboard;